import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Check, ArrowRight, Star, Info } from 'lucide-react';

const UNLIMITED_THRESHOLD = 999998;

const buildCmmsFeatures = (plan, t) => {
  const feats = [];
  if (plan.includedAssets != null)
    feats.push(plan.includedAssets >= UNLIMITED_THRESHOLD
      ? t('pricing.feat_unlimitedAssets')
      : t('pricing.feat_assets', { n: plan.includedAssets }));
  if (plan.includedUsers != null)
    feats.push(plan.includedUsers >= UNLIMITED_THRESHOLD
      ? t('pricing.feat_unlimitedUsers')
      : t('pricing.feat_users', { n: plan.includedUsers }));
  if (plan.includedLocations != null)
    feats.push(plan.includedLocations >= UNLIMITED_THRESHOLD
      ? t('pricing.feat_unlimitedLocations')
      : t('pricing.feat_locations', { n: plan.includedLocations }));
  if (plan.includedBranches != null)
    feats.push(plan.includedBranches >= UNLIMITED_THRESHOLD
      ? t('pricing.feat_unlimitedBranches')
      : t('pricing.feat_branches', { n: plan.includedBranches }));
  if (plan.features) {
    try {
      const extra = JSON.parse(plan.features);
      if (Array.isArray(extra)) feats.push(...extra.map(String));
    } catch {}
  }
  return feats;
};

const buildChatbotFeatures = (plan, t) => {
  const feats = [];
  if (plan.messagesPerMonth != null) {
    if (plan.messagesPerMonth === -1) {
      feats.push(t('pricing.feat_unlimitedMsgs'));
    } else {
      const n = Number(plan.messagesPerMonth).toLocaleString();
      feats.push(t('pricing.feat_msgs', { n }));
    }
  }
  if (plan.maxChatbots != null) {
    if (plan.maxChatbots === -1) {
      feats.push(t('pricing.feat_unlimitedChatbots'));
    } else if (plan.maxChatbots === 1) {
      feats.push(t('pricing.feat_chatbot', { n: 1 }));
    } else {
      feats.push(t('pricing.feat_chatbots', { n: plan.maxChatbots }));
    }
  }
  if (plan.maxDocumentsPerChatbot != null) {
    if (plan.maxDocumentsPerChatbot === -1) {
      feats.push(t('pricing.feat_unlimitedDocs'));
    } else if (plan.maxDocumentsPerChatbot === 1) {
      feats.push(t('pricing.feat_doc', { n: 1 }));
    } else {
      feats.push(t('pricing.feat_docs', { n: plan.maxDocumentsPerChatbot }));
    }
  }
  if (plan.includedAgents == null) {
    feats.push(t('pricing.feat_agents', { n: '∞' }));
  } else if (plan.includedAgents > 0) {
    feats.push(plan.includedAgents === 1
      ? t('pricing.feat_agent', { n: 1 })
      : t('pricing.feat_agents', { n: plan.includedAgents }));
  }
  feats.push(t('pricing.feat_channels'));
  if (plan.features) {
    try {
      const extra = JSON.parse(plan.features);
      if (Array.isArray(extra)) feats.push(...extra.map(String));
    } catch {}
  }
  return feats;
};

const PricingSection = () => {
  const { t } = useTranslation();
  const [billingCycle, setBillingCycle] = useState('annual');
  const [cmmsPlans, setCmmsPlans] = useState(null);
  const [chatbotPlans, setChatbotPlans] = useState(null);
  const [loadingPlans, setLoadingPlans] = useState(true);

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const [cmmsRes, chatbotRes] = await Promise.all([
          fetch('/api/subscriptions/cmms/plans'),
          fetch('/api/subscriptions/chatbot/plans'),
        ]);
        if (!cmmsRes.ok || !chatbotRes.ok) throw new Error('Failed to fetch plans');
        const cmmsData = await cmmsRes.json();
        const chatbotData = await chatbotRes.json();

        const cmmsArr = (Array.isArray(cmmsData) ? cmmsData : (cmmsData.content ?? []))
          .filter(p => p.isActive && !p.isTrial)
          .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));

        const chatbotArr = (Array.isArray(chatbotData) ? chatbotData : (chatbotData.content ?? []))
          .filter(p => p.isActive && !p.isTrial)
          .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));

        setCmmsPlans(cmmsArr.map((plan, idx, arr) => ({
          name: plan.planName,
          monthly: parseFloat(plan.monthlyPrice) || 0,
          annual: parseFloat(plan.annualPrice) || Math.round(parseFloat(plan.monthlyPrice) * 12 * 0.8),
          description: plan.description || '',
          features: buildCmmsFeatures(plan, t),
          popular: arr.length > 1 && idx === 1,
          disclaimer: t('pricing.extraSlots'),
          cta: (plan.planCode || '').toLowerCase().includes('enterprise')
            ? t('pricing.contactSales')
            : t('pricing.startTrial'),
        })));

        setChatbotPlans(chatbotArr.map((plan, idx, arr) => ({
          name: plan.planName,
          monthly: parseFloat(plan.monthlyPrice) || 0,
          annual: parseFloat(plan.annualPrice) || Math.round(parseFloat(plan.monthlyPrice) * 12 * 0.8),
          description: plan.description || '',
          features: buildChatbotFeatures(plan, t),
          popular: arr.length > 1 && idx === 1,
          disclaimer: plan.overageEnabled && parseFloat(plan.pricePerAdditional1k) > 0
            ? t('pricing.extraMessagesRate', { price: plan.pricePerAdditional1k })
            : t('pricing.extraMessages'),
          cta: (plan.planCode || '').toLowerCase().includes('enterprise')
            ? t('pricing.contactSales')
            : t('pricing.startTrial'),
        })));
      } catch (err) {
        console.error('Failed to fetch pricing plans:', err);
      } finally {
        setLoadingPlans(false);
      }
    };
    fetchPlans();
  }, [t]);

  const fallbackCmmsPlans = [
    {
      name: t('pricing.basic'),
      monthly: 99,
      annual: 950,
      description: t('pricing.descBasicCmms'),
      features: [
        t('pricing.feat_assets', { n: 50 }),
        t('pricing.feat_users', { n: 5 }),
        t('pricing.feat_workOrders'),
        t('pricing.feat_basicReports'),
        t('pricing.feat_emailSupport'),
        t('pricing.feat_mobileApp'),
      ],
      disclaimer: t('pricing.extraSlots'),
      cta: t('pricing.startTrial'),
      popular: false,
    },
    {
      name: t('pricing.pro'),
      monthly: 299,
      annual: 2870,
      description: t('pricing.descProCmms'),
      features: [
        t('pricing.feat_assets', { n: 200 }),
        t('pricing.feat_users', { n: 20 }),
        t('pricing.feat_advWorkOrders'),
        t('pricing.feat_preventive'),
        t('pricing.feat_inventory'),
        t('pricing.feat_prioritySupport'),
        t('pricing.feat_customReports'),
        t('pricing.feat_apiAccess'),
      ],
      disclaimer: t('pricing.extraSlots'),
      cta: t('pricing.startTrial'),
      popular: true,
    },
    {
      name: t('pricing.enterprise'),
      monthly: 999,
      annual: 9590,
      description: t('pricing.descEnterpriseCmms'),
      features: [
        t('pricing.feat_unlimitedAssets'),
        t('pricing.feat_unlimitedUsers'),
        t('pricing.feat_allPro'),
        t('pricing.feat_customInteg'),
        t('pricing.feat_dedicatedMgr'),
        t('pricing.feat_phone247'),
        t('pricing.feat_advSecurity'),
        t('pricing.feat_sla'),
      ],
      disclaimer: t('pricing.extraSlots'),
      cta: t('pricing.contactSales'),
      popular: false,
    },
  ];

  const fallbackChatbotPlans = [
    {
      name: t('pricing.basic'),
      monthly: 49,
      annual: 470,
      description: t('pricing.descBasicChatbot'),
      features: [
        t('pricing.feat_msgs', { n: '10,000' }),
        t('pricing.feat_chatbot', { n: 1 }),
        t('pricing.feat_docs', { n: 5 }),
        t('pricing.feat_agent', { n: 1 }),
        t('pricing.feat_channels'),
        t('pricing.feat_emailSupport'),
        t('pricing.feat_basicAnalytics'),
      ],
      disclaimer: t('pricing.extraMessages'),
      cta: t('pricing.startTrial'),
      popular: false,
    },
    {
      name: t('pricing.pro'),
      monthly: 149,
      annual: 1430,
      description: t('pricing.descProChatbot'),
      features: [
        t('pricing.feat_msgs', { n: '50,000' }),
        t('pricing.feat_chatbots', { n: 5 }),
        t('pricing.feat_docs', { n: 50 }),
        t('pricing.feat_agents', { n: 3 }),
        t('pricing.feat_channels'),
        t('pricing.feat_prioritySupport'),
        t('pricing.feat_advAnalytics'),
        t('pricing.feat_customBranding'),
        t('pricing.feat_apiAccess'),
      ],
      disclaimer: t('pricing.extraMessages'),
      cta: t('pricing.startTrial'),
      popular: true,
    },
    {
      name: t('pricing.enterprise'),
      monthly: 499,
      annual: 4790,
      description: t('pricing.descEnterpriseChatbot'),
      features: [
        t('pricing.feat_unlimMsgs'),
        t('pricing.feat_unlimitedChatbots'),
        t('pricing.feat_unlimDocs'),
        t('pricing.feat_agents', { n: '∞' }),
        t('pricing.feat_channels'),
        t('pricing.feat_support247'),
        t('pricing.feat_whiteLabel'),
        t('pricing.feat_customInteg'),
        t('pricing.feat_sla'),
      ],
      disclaimer: t('pricing.extraMessages'),
      cta: t('pricing.contactSales'),
      popular: false,
    },
  ];

  const displayCmmsPlans = cmmsPlans ?? fallbackCmmsPlans;
  const displayChatbotPlans = chatbotPlans ?? fallbackChatbotPlans;

  return (
    <section className="section bg-gray-50">
      <div className="container-custom">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            {t('pricing.title1')} <span className="gradient-text">{t('pricing.title2')}</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
            {t('pricing.subtitle')}
          </p>

          {/* Billing Toggle */}
          <div className="inline-flex items-center gap-4 bg-white rounded-full p-2 shadow-sm">
            <button
              onClick={() => setBillingCycle('monthly')}
              className={`px-6 py-2 rounded-full font-medium transition-all ${
                billingCycle === 'monthly'
                  ? 'bg-primary-600 text-white'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {t('pricing.monthly')}
            </button>
            <button
              onClick={() => setBillingCycle('annual')}
              className={`px-6 py-2 rounded-full font-medium transition-all inline-flex items-center ${
                billingCycle === 'annual'
                  ? 'bg-primary-600 text-white'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {t('pricing.annual')}
              <span className="ms-3 text-xs bg-green-100 text-green-700 px-3 py-1 rounded-full">
                {t('pricing.save')}
              </span>
            </button>
          </div>
        </motion.div>

        {loadingPlans ? (
          <div className="flex justify-center py-20">
            <div className="w-10 h-10 border-4 border-primary-600 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <>
            {/* CMMS Plans */}
            <div className="mb-16">
              <h3 className="text-2xl font-bold text-center text-gray-900 mb-8">{t('pricing.cmmsPlans')}</h3>
              <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
                {displayCmmsPlans.map((plan, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className={`pricing-card relative ${plan.popular ? 'pricing-card-featured' : ''}`}
                  >
                    {plan.popular && (
                      <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-primary-600 text-white px-4 py-1 rounded-full text-sm font-medium flex items-center gap-1">
                        <Star className="w-4 h-4" />
                        {t('pricing.popular')}
                      </div>
                    )}

                    <div className="text-center mb-6">
                      <h4 className="text-2xl font-bold text-gray-900 mb-2">{plan.name}</h4>
                      <p className="text-gray-600 text-sm mb-4">{plan.description}</p>
                      <div className="flex items-baseline justify-center gap-2">
                        <span className="text-5xl font-bold text-gray-900">
                          ${billingCycle === 'monthly' ? plan.monthly : Math.round(plan.annual / 12)}
                        </span>
                        <span className="text-gray-600">{t('pricing.perMonth')}</span>
                      </div>
                      {billingCycle === 'annual' && (
                        <p className="text-sm text-green-600 mt-2">
                          ${plan.annual} {t('pricing.billedAnnually')}
                        </p>
                      )}
                    </div>

                    <ul className="space-y-3 mb-4">
                      {plan.features.map((feature, i) => (
                        <li key={i} className="flex items-start gap-3">
                          <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-700">{feature}</span>
                        </li>
                      ))}
                    </ul>

                    {plan.disclaimer && (
                      <p className="flex items-start gap-1.5 text-xs text-gray-500 italic mb-6">
                        <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-gray-400" />
                        {plan.disclaimer}
                      </p>
                    )}

                    <Link
                      to="/signup"
                      className={`w-full btn ${plan.popular ? 'btn-primary' : 'btn-outline'}`}
                    >
                      {plan.cta}
                    </Link>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Chatbot Plans */}
            <div>
              <h3 className="text-2xl font-bold text-center text-gray-900 mb-8">{t('pricing.chatbotPlans')}</h3>
              <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
                {displayChatbotPlans.map((plan, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className={`pricing-card relative ${plan.popular ? 'pricing-card-featured' : ''}`}
                  >
                    {plan.popular && (
                      <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-secondary-600 text-white px-4 py-1 rounded-full text-sm font-medium flex items-center gap-1">
                        <Star className="w-4 h-4" />
                        {t('pricing.popular')}
                      </div>
                    )}

                    <div className="text-center mb-6">
                      <h4 className="text-2xl font-bold text-gray-900 mb-2">{plan.name}</h4>
                      <p className="text-gray-600 text-sm mb-4">{plan.description}</p>
                      <div className="flex items-baseline justify-center gap-2">
                        <span className="text-5xl font-bold text-gray-900">
                          ${billingCycle === 'monthly' ? plan.monthly : Math.round(plan.annual / 12)}
                        </span>
                        <span className="text-gray-600">{t('pricing.perMonth')}</span>
                      </div>
                      {billingCycle === 'annual' && (
                        <p className="text-sm text-green-600 mt-2">
                          ${plan.annual} {t('pricing.billedAnnually')}
                        </p>
                      )}
                    </div>

                    <ul className="space-y-3 mb-4">
                      {plan.features.map((feature, i) => (
                        <li key={i} className="flex items-start gap-3">
                          <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-700">{feature}</span>
                        </li>
                      ))}
                    </ul>

                    {plan.disclaimer && (
                      <p className="flex items-start gap-1.5 text-xs text-gray-500 italic mb-6">
                        <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-gray-400" />
                        {plan.disclaimer}
                      </p>
                    )}

                    <Link
                      to="/signup"
                      className={`w-full btn ${plan.popular ? 'btn-secondary' : 'btn-outline'}`}
                    >
                      {plan.cta}
                    </Link>
                  </motion.div>
                ))}
              </div>
            </div>
          </>
        )}

        {/* View Full Pricing */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mt-12"
        >
          <Link to="/pricing" className="btn btn-outline text-lg">
            {t('pricing.viewDetailed')}
            <ArrowRight className="w-5 h-5 ml-2" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default PricingSection;
